<template>
  <div class="youtube-player" @click="playPause">
    <div ref="playerRef" class="youtube-player__player" />
    <ProgressBar class="youtube-player__progress-bar" :value="progress" />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watchEffect } from 'vue'

import ProgressBar from '../../../shared/components/dumb/ProgressBar.vue'
import {
  useYoutubePlayer,
  useYoutubePlayerProgress,
  useYoutubePlayerState,
  YT_CONSTANTS,
} from '../../../shared/composables/useYoutubePlayer'

export default defineComponent({
  components: { ProgressBar },
  props: {
    videoId: {
      type: String,
      required: true,
    },
  },
  emits: {
    ended: () => true,
  },
  setup(props, { emit }) {
    const playerRef = ref<HTMLDivElement>()
    const youtubePlayer = useYoutubePlayer(
      playerRef,
      computed(() => props.videoId),
      {
        playerVars: {
          playsinline: 1,
          fs: 0,
          disablekb: 1,
          controls: 0,
          rel: 0,
          modestbranding: 1,
          showinfo: 0,
          autoplay: 0,
          enablejsapi: 1,
        },
      },
    )

    const youtubePlayerState = useYoutubePlayerState(youtubePlayer)

    watchEffect(() => {
      if (youtubePlayerState.value === YT_CONSTANTS.PLAYER_STATE.ENDED) {
        emit('ended')
      }
    })

    const playPause = (): void => {
      if (youtubePlayerState.value === YT_CONSTANTS.PLAYER_STATE.PLAYING) {
        youtubePlayer.value?.pauseVideo()
      } else {
        youtubePlayer.value?.playVideo()
      }
    }

    return { playerRef, playPause, progress: useYoutubePlayerProgress(youtubePlayer) }
  },
})
</script>

<style lang="postcss">
.youtube-player {
  display: flex;
  max-width: 478px;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  gap: 1rem;

  &__player {
    width: 100%;
    aspect-ratio: 16 / 9;
    pointer-events: none;
  }

  &__progress-bar {
    width: calc(100% - 2rem);
  }
}
</style>
